import React, { useEffect, useRef, useState } from 'react';

const STICKY_TOP_FALLBACK = 72;
const STICKY_MIN_WIDTH = 769;

const getNavbarHeight = () => {
  const navbar = document.querySelector('.navbar-width.sticky-top, .tech-central-main-navbar');
  if (!navbar) {
    return STICKY_TOP_FALLBACK;
  }
  return Math.ceil(navbar.getBoundingClientRect().height) || STICKY_TOP_FALLBACK;
};

const BootcampContextualFilters = ({
  activeSubTab,
  overviewFilters,
  onOverviewFiltersChange,
  individualFilters,
  onIndividualFiltersChange,
  filterOptions,
}) => {
  const slotRef = useRef(null);
  const sentinelRef = useRef(null);
  const stickyTopRef = useRef(STICKY_TOP_FALLBACK);
  const [isSticky, setIsSticky] = useState(false);

  const updateIndividualFilter = (key, value) => {
    onIndividualFiltersChange((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const section = slotRef.current?.closest('.bootcamp-stats-section');
    const sentinel = sentinelRef.current;

    const syncStickyTop = () => {
      stickyTopRef.current = getNavbarHeight();
      section?.style.setProperty('--bootcamp-filter-sticky-top', `${stickyTopRef.current}px`);
    };

    const updateStickyState = () => {
      if (window.innerWidth < STICKY_MIN_WIDTH || !sentinel) {
        setIsSticky(false);
        return;
      }

      setIsSticky(sentinel.getBoundingClientRect().top < stickyTopRef.current);
    };

    syncStickyTop();
    updateStickyState();

    window.addEventListener('resize', syncStickyTop);
    window.addEventListener('resize', updateStickyState);
    window.addEventListener('scroll', updateStickyState, { passive: true });

    return () => {
      window.removeEventListener('resize', syncStickyTop);
      window.removeEventListener('resize', updateStickyState);
      window.removeEventListener('scroll', updateStickyState);
    };
  }, [activeSubTab]);

  const filterPanelClassName =
    'bootcamp-filter-panel bootcamp-contextual-filters bootcamp-context-filter-card solution-demo-filter-sticky';

  return (
    <>
      <div ref={sentinelRef} className="bootcamp-filter-sticky-sentinel" aria-hidden="true" />
      <div
        ref={slotRef}
        className={`bootcamp-contextual-filter-slot ${isSticky ? 'is-sticky' : ''}`}
        aria-live="polite"
      >
        {activeSubTab === 'overview' ? (
          <div
            key="overview-filters"
            className={`${filterPanelClassName} bootcamp-contextual-filters-overview`}
          >
            <div className="bootcamp-filter-grid">
              <label className="bootcamp-filter-field" htmlFor="bootcamp-filter-name">
                <span>Bootcamp Name</span>
                <select
                  id="bootcamp-filter-name"
                  value={overviewFilters.bootcampName}
                  onChange={(event) =>
                    onOverviewFiltersChange((prev) => ({ ...prev, bootcampName: event.target.value }))
                  }
                >
                  <option value="All">All Bootcamps</option>
                  {filterOptions.bootcampNames.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-filter-region">
                <span>Region</span>
                <select
                  id="bootcamp-filter-region"
                  value={overviewFilters.region}
                  onChange={(event) =>
                    onOverviewFiltersChange((prev) => ({ ...prev, region: event.target.value }))
                  }
                >
                  <option value="All">All Regions</option>
                  {filterOptions.regions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-filter-org">
                <span>Org</span>
                <select
                  id="bootcamp-filter-org"
                  value={overviewFilters.org}
                  onChange={(event) =>
                    onOverviewFiltersChange((prev) => ({ ...prev, org: event.target.value }))
                  }
                >
                  <option value="All">All Orgs</option>
                  {filterOptions.orgs.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-filter-status">
                <span>Certification Status</span>
                <select
                  id="bootcamp-filter-status"
                  value={overviewFilters.certificationStatus}
                  onChange={(event) =>
                    onOverviewFiltersChange((prev) => ({
                      ...prev,
                      certificationStatus: event.target.value,
                    }))
                  }
                >
                  <option value="All">All Statuses</option>
                  {filterOptions.certifications.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-filter-cohort">
                <span>Cohort</span>
                <select
                  id="bootcamp-filter-cohort"
                  value={overviewFilters.cohort}
                  onChange={(event) =>
                    onOverviewFiltersChange((prev) => ({ ...prev, cohort: event.target.value }))
                  }
                >
                  <option value="All">All Cohorts</option>
                  {filterOptions.cohorts.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        ) : (
          <div
            key="individual-filters"
            className={`${filterPanelClassName} bootcamp-contextual-filters-individual`}
          >
            <div className="bootcamp-individual-filters">
              <label className="bootcamp-filter-field bootcamp-filter-field-search" htmlFor="bootcamp-search">
                <span>Search</span>
                <input
                  id="bootcamp-search"
                  type="text"
                  placeholder="Search participant, email, company, champion"
                  value={individualFilters.search}
                  onChange={(event) => updateIndividualFilter('search', event.target.value)}
                />
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-ind-region">
                <span>Region</span>
                <select
                  id="bootcamp-ind-region"
                  value={individualFilters.region}
                  onChange={(event) => updateIndividualFilter('region', event.target.value)}
                >
                  <option value="All">All Regions</option>
                  {filterOptions.regions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-ind-org">
                <span>Org</span>
                <select
                  id="bootcamp-ind-org"
                  value={individualFilters.org}
                  onChange={(event) => updateIndividualFilter('org', event.target.value)}
                >
                  <option value="All">All Orgs</option>
                  {filterOptions.orgs.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-ind-cohort">
                <span>Cohort</span>
                <select
                  id="bootcamp-ind-cohort"
                  value={individualFilters.cohort}
                  onChange={(event) => updateIndividualFilter('cohort', event.target.value)}
                >
                  <option value="All">All Cohorts</option>
                  {filterOptions.cohorts.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-ind-attendance">
                <span>Attendance</span>
                <select
                  id="bootcamp-ind-attendance"
                  value={individualFilters.attendance}
                  onChange={(event) => updateIndividualFilter('attendance', event.target.value)}
                >
                  <option value="All">Attendance: All</option>
                  {filterOptions.attendanceValues.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-ind-status">
                <span>Certification Status</span>
                <select
                  id="bootcamp-ind-status"
                  value={individualFilters.certificationStatus}
                  onChange={(event) => updateIndividualFilter('certificationStatus', event.target.value)}
                >
                  <option value="All">All Statuses</option>
                  {filterOptions.certifications.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="bootcamp-filter-field" htmlFor="bootcamp-ind-champion">
                <span>Champion</span>
                <select
                  id="bootcamp-ind-champion"
                  value={individualFilters.champion}
                  onChange={(event) => updateIndividualFilter('champion', event.target.value)}
                >
                  <option value="All">All Champions</option>
                  {filterOptions.champions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BootcampContextualFilters;
